import React from "react";
import { observer } from "mobx-react";
import locale from "@bonliva-crm/locale";
import {
  FormControl,
  FormLabel,
  FormGroup,
  Paper,
  createStyles,
  Grid,
  IconButton,
  withStyles,
  Chip,
  TextField
} from "@material-ui/core";
import { TabBadge } from "@components";
import { Select, Text } from "@components/formx_item_form";
import { Form, FormxDebugger, ToggleButtonGroup, Switch } from "@components/formx";
import FormSection from "@components/forms/common/FormSection";
import { TabFormLayout } from "@components/forms/TabFormLayout";
import { AccommodationEditorStore } from "./AccommodationEditorStore";
import { Classes } from "@theme";
import { Add, Remove } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import {
  AccommodationType,
  AccommodationPriceType,
  AccommodationOfferOptions
} from "@graphql";

type OwnProps = {
  store: AccommodationEditorStore;
  onClose?: () => void;
};

type Props = OwnProps & {
  classes: Classes<typeof styles>;
};

@observer
class AccommodationForm extends React.Component<Props> {
  renderMainTab = () => {
    const {
      classes,
      store: { handleChangeChip, addOffer, removeOffer, handleImg },
      store
    } = this.props;
    return (
      <Form store={store.form} allowUpdates>
        <div className={classes.mainTab}>
          <FormSection>
            <ToggleButtonGroup
              name="Type"
              wrap
              label={locale.AccommodationType}
              options={Object.values(AccommodationType).map((v) => ({
                label: locale.accommodationType[v],
                value: v
              }))}
            />

            <img
              className={classes.img}
              src={store.form.values.ImgUrl}
              alt={store.form.values.Name}
              onClick={handleImg}
            />

            <Select
              label={locale.Responsible}
              name="ResponsiblePersonRef"
              options={store.enabledUsers}
            />
            <Text label={locale.Name} name="Name" />

            <Autocomplete
              multiple
              freeSolo
              options={store.form.values.Email}
              defaultValue={store.form.values.Email}
              renderTags={(value, getTagProps) => {
                return value.map((option, index) => (
                  <Chip label={option} {...getTagProps({ index })} />
                ));
              }}
              renderInput={(params) => <TextField {...params} label={locale.Email} />}
              onChange={(_, chips) => handleChangeChip(chips, "Email")}
            />

            <Autocomplete
              multiple
              freeSolo
              options={store.form.values.Phone}
              defaultValue={store.form.values.Phone}
              renderTags={(value, getTagProps) => {
                return value.map((option, index) => (
                  <Chip label={option} {...getTagProps({ index })} />
                ));
              }}
              renderInput={(params) => <TextField {...params} label={locale.Phone} />}
              onChange={(_, chips) => handleChangeChip(chips, "Phone")}
            />

            <Select
              label={locale.Country}
              required
              name="Address.Country"
              options={store.allCountries.map((v) => ({
                label: locale.countries[v],
                value: v
              }))}
            />

            <Text label={locale.City} name="Address.City" />

            <Text label={locale.StreetName} name="Address.StreetName" />

            <Text label={locale.ZipCode} name="Address.ZipCode" />

            <Text label={locale.CheckIn} name="CheckIn" />

            <Text label={locale.CheckOut} name="CheckOut" />

            <Text label={locale.Site} name="Site" />

            <Text
              label={locale.Instruction}
              name="Instruction"
              TextFieldProps={{
                multiline: true,
                helperText: locale["Press Enter for a new line"]
              }}
            />

            <Text
              label={locale.CancellationPolicy}
              name="CancellationPolicy"
              TextFieldProps={{
                multiline: true,
                helperText: locale["Press Enter for a new line"]
              }}
            />
            <Grid item container spacing={2} xs={12}>
              <Grid item>
                <Switch name="envCertification" label={locale.EnvCertification} />
              </Grid>
              <Grid item>
                <Switch name="preferredChoice" label={locale.PreferredChoice} />
              </Grid>
              <Grid item xs={12}>
                {!!store.form.values.envCertification && (
                  <Text name="envCertificationName" label={locale.EnvCertificationName} />
                )}
              </Grid>
            </Grid>
          </FormSection>
          <div className={classes.offersWrap}>
            {store.form.values.Offers?.map(
              (el, i) =>
                !el.Deleted && (
                  <Grid key={i} item container spacing={2} xs={12} component={Paper}>
                    <Grid item xs={4}>
                      <Text label={locale.RoomsType} name={`Offers[${i}].Name`} />
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl>
                        <FormLabel component="legend">{locale.Price}</FormLabel>
                        <FormGroup row className={classes.priceWrap}>
                          <Text
                            name={`Offers[${i}].Price`}
                            TextFieldProps={{
                              type: "number"
                            }}
                          />
                          <Select
                            clearable={false}
                            label={""}
                            name={`Offers[${i}].PriceType`}
                            options={Object.values(AccommodationPriceType).map((v) => ({
                              label: locale.accommodationPriceType[v],
                              value: v
                            }))}
                          />
                        </FormGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <Text
                        label={locale.Rooms}
                        name={`Offers[${i}].Rooms`}
                        TextFieldProps={{ type: "number" }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Text
                        label={locale.Beds}
                        name={`Offers[${i}].Beds`}
                        TextFieldProps={{ type: "number" }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Text
                        label={locale.Area}
                        name={`Offers[${i}].Area`}
                        TextFieldProps={{ type: "number" }}
                      />
                    </Grid>
                    <Grid xs={12} item>
                      <Select
                        label={locale.Options}
                        multi
                        options={Object.values(AccommodationOfferOptions).map((v) => ({
                          label: locale.accommodationOfferOptions[v],
                          value: v
                        }))}
                        name={`Offers[${i}].Options`}
                        transformValue={(v) => v.value}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <IconButton aria-label="add" onClick={addOffer}>
                        <Add fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="delete"
                        disabled={
                          store.form.values.Offers?.filter((x) => !x.Deleted)?.length ===
                          1
                        }
                        onClick={() => removeOffer(i)}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                )
            )}
          </div>
        </div>
      </Form>
    );
  };

  render() {
    const {
      onClose,
      store: { form }
    } = this.props;
    return (
      <TabFormLayout
        blockId="accommodation_form"
        canSubmit={form.canSubmit}
        isDirty={form.dirty}
        isSubmitting={form.isSubmitting}
        onSubmit={form.onSubmit}
        onReset={form.onResetHandler}
        onClose={onClose}
        tabs={[
          {
            label: <TabBadge label={locale.Main} hasErrors={false} />,
            value: "main",
            content: this.renderMainTab()
          }
        ]}
      >
        <FormxDebugger store={form} />
      </TabFormLayout>
    );
  }
}

const styles = () =>
  createStyles({
    mainTab: {
      display: "flex",
      justifyContent: "space-between",
      "& > section > div > *:not(:last-child)": {
        marginBottom: 16
      }
    },
    priceWrap: {
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "flex-end"
    },
    img: {
      display: "block",
      width: "200px",
      height: "200px",
      objectFit: "cover",
      cursor: "pointer",
      background: "#eee"
    },
    offersWrap: {
      maxWidth: "50%",
      padding: "5px 16px",
      "& > div:not(:last-child)": {
        marginBottom: "5px"
      }
    }
  });

export default withStyles(styles)(AccommodationForm);
